import React, { useState, useRef, useEffect } from 'react';
import { Sun, Moon, Globe, Check, ChevronDown } from 'lucide-react';
import { useTheme, useLang, SUPPORTED_LANGS } from '../context/UIContext';

export default function UIControls({ compact = false }) {
  const { theme, setTheme } = useTheme();
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const current = SUPPORTED_LANGS.find((l) => l.code === lang) || SUPPORTED_LANGS[0];

  return (
    <div className={`flex items-center ${compact ? 'gap-1.5' : 'gap-2'}`}>
      {/* Theme toggle */}
      <button
        type="button"
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        data-testid="theme-toggle"
        aria-label={theme === 'dark' ? 'Attiva tema chiaro' : 'Attiva tema scuro'}
        title={theme === 'dark' ? 'Tema chiaro' : 'Tema scuro'}
        className="w-9 h-9 rounded-full border border-white/15 text-white/80 hover:border-lime hover:text-lime transition-colors flex items-center justify-center"
      >
        {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
      </button>

      {/* Language switcher */}
      <div className="relative" ref={ref}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid="lang-toggle"
          aria-label="Cambia lingua"
          className="h-9 px-2.5 rounded-full border border-white/15 text-white/80 hover:border-lime hover:text-lime transition-colors flex items-center gap-1.5 text-xs font-semibold"
        >
          <Globe size={14} />
          <span>{current.label}</span>
          <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div
            data-testid="lang-dropdown"
            className="absolute right-0 top-full mt-2 min-w-[150px] rounded-xl bg-[#0e1838] border border-white/15 shadow-2xl py-1 z-50"
          >
            {SUPPORTED_LANGS.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => {
                  setLang(l.code);
                  setOpen(false);
                }}
                data-testid={`lang-option-${l.code}`}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-sm transition-colors ${
                  l.code === lang ? 'text-lime' : 'text-white/80 hover:bg-white/5'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span aria-hidden>{l.flag}</span>
                  {l.name}
                </span>
                {l.code === lang && <Check size={14} />}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
